import React, { useCallback, useEffect, useMemo } from "react";

import { Model } from 'survey-core';
import { Survey as SurveyUI } from 'survey-react-ui';

import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { OutletContextModel } from "../../models/OutletContextModel";
import { GraphModel } from "../../models/GraphModel";
import { SurveyResultModel } from "../../models/SurveyResultModel";
import { ContactDataModel } from "../../models/ContactDataModel";
import { setDocument } from "../../core/firebase/firebaseFirestore";
import { useMutation } from "@tanstack/react-query";

const fullNameRegex = /^[a-zA-ZñÑáÁéÉíÍóÓúÚüÜ' -]{3,150}$/;
const phoneRegex = /^[0-9]{10}$/;

const formJson = {
    title: 'Captura de datos',
    showQuestionNumbers: 'none',
    checkErrorsMode: 'onValueChanged',
    completeText: 'Confirmar',
    showCompletedPage: false,
    showPrevButton: true,
    pages: [
        {
            title: 'Para ver resultados completos proporciona tus datos de contacto',
            elements: [
                {
                    // defaultValue: 'Pancho Lopez',
                    type: 'text',
                    name: 'fullName',
                    title: 'Nombre completo',
                    placeholder: 'Ingresar nombre completo',
                    isRequired: true,
                    requiredErrorText: 'Campo requerido',
                    validators: [
                        {
                            type: 'regex',
                            regex: fullNameRegex,
                            text: 'Ingresar nombre válido'
                        }
                    ]
                },
                {
                    // defaultValue: 'test@prueba.com',
                    type: 'text',
                    name: 'email',
                    title: 'Correo electrónico',
                    placeholder: 'Ingresar correo electrónico',
                    isRequired: true,
                    requiredErrorText: 'Campo requerido',
                    validators: [
                        {
                            type: 'email',
                            text: 'Ingresar correo electrónico válido'
                        }
                    ]
                },
                {
                    // defaultValue: '2222222222',
                    type: 'text',
                    name: 'phone',
                    title: 'Teléfono',
                    placeholder: 'Ingresar teléfono',
                    isRequired: true,
                    requiredErrorText: 'Campo requerido',
                    validators: [
                        {
                            type: 'regex',
                            regex: phoneRegex,
                            text: 'Ingresar teléfono a 10 dígitos'
                        }
                    ]
                }
            ]
        }
    ]
};

const resultsCollectionName = 'results';

const ContactData = () => {
    const survey = useMemo(() => new Model(formJson), []);

    const navigate = useNavigate();
    const location = useLocation();

    const outletContext: OutletContextModel = useOutletContext();

    const params: GraphModel = location.state;

    survey.applyTheme(outletContext.theme);
    survey.addNavigationItem({
        id: "return_to_results",
        title: "Regresar",
        visibleIndex: 49, // "Complete" button has the visibleIndex 50.
        action: () => {
            navigate(-1);
        }
    });
    survey.onComplete.add(
        useCallback(async (sender: Model) => {
            const results: ContactDataModel = sender.data;
            const surveyData: SurveyResultModel = {
                ...params.data,
                hasContactData: true,
                contactData: results
            }
            survey.clear();
            updateResults.mutate(surveyData);
        }, [navigate, params, survey])
    );

    const onUpdateResults = async (surveyData: any) => {
        return await setDocument(params.documentId, resultsCollectionName, surveyData);
    }

    const updateResults = useMutation({
        mutationFn: onUpdateResults,
        onSuccess: (response) => {
            if (response) {
                navigate(
                    '/resultado-diagnostico',
                    {
                        state: {
                            ...params,
                            type: 'complete'
                        }
                    }
                );
            }
        }
    });

    useEffect(() => {
        if (!params) {
            navigate('/encuesta', { replace: true });
        }
    }, [navigate, params]);

    return (
        <div className="full-height-container ">
            <SurveyUI model={survey} />
        </div>
    );
}

export default ContactData;